import React from 'react';
import { useSelector } from 'react-redux';
import { Chip, Box, Tooltip } from '@mui/material';
import { FilterAlt, Timeline } from '@mui/icons-material';
import { getPlatformLabel } from '../../config/platforms';
import languages from '../../config/languages';
import sentiments from '../../config/sentiments';

function ChartInteractionIndicator({ filters, onRemoveFilter, readOnlyKeys = [] }) {
    const tags = useSelector(state => state.tag.list || []);
    const hasInteractiveFilters = filters.platform || filters.jobs || filters.sentiment || filters.language || (filters.tag && filters.tag.value && filters.tag.value.length > 0) || (filters.date && filters.date.start && filters.date.end);
    
    if (!hasInteractiveFilters) return null;
    
    const formatDateRange = (date) => {
        if (date.start === date.end) {
            return new Date(date.start).toLocaleDateString();
        }
        return `${new Date(date.start).toLocaleDateString()} - ${new Date(date.end).toLocaleDateString()}`;
    };
    
    const canRemove = (key) => !readOnlyKeys.includes(key) && !!onRemoveFilter;

    return (
        <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Timeline color="primary" fontSize="small" />
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {filters.platform && (
                    <Tooltip title="Filter applied by clicking on chart">
                        <Chip
                            icon={<FilterAlt />}
                            label={`Platform: ${getPlatformLabel(filters.platform) || filters.platform}`}
                            onDelete={canRemove('platform') ? () => onRemoveFilter('platform') : undefined}
                            variant="filled"
                            size="small"
                            sx={{ 
                                backgroundColor: '#FFD700',
                                color: '#333333',
                                '& .MuiChip-label': { 
                                    fontWeight: 'normal',
                                    color: '#333333'
                                },
                                '& .MuiChip-icon': {
                                    color: '#333333'
                                },
                                '& .MuiChip-deleteIcon': {
                                    color: '#333333',
                                    '&:hover': {
                                        color: '#000000'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#FFC107'
                                }
                            }}
                        />
                    </Tooltip>
                )}
                {filters.jobs && filters.jobs.length > 0 && (
                    <Tooltip title="Job filter applied by clicking on chart">
                        <Chip
                            icon={<FilterAlt />}
                            label={`Job: ${filters.jobs[0]}`}
                            onDelete={canRemove('jobs') ? () => onRemoveFilter('jobs') : undefined}
                            variant="filled"
                            size="small"
                            sx={{ 
                                backgroundColor: '#FFD700',
                                color: '#333333',
                                '& .MuiChip-label': { 
                                    fontWeight: 'normal',
                                    color: '#333333'
                                },
                                '& .MuiChip-icon': {
                                    color: '#333333'
                                },
                                '& .MuiChip-deleteIcon': {
                                    color: '#333333',
                                    '&:hover': {
                                        color: '#000000'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#FFC107'
                                }
                            }}
                        />
                    </Tooltip>
                )}
                {filters.tag && filters.tag.value && filters.tag.value.length > 0 && (
                    <Tooltip title="Tags filter applied by clicking on chart">
                        <Chip
                            icon={<FilterAlt />}
                            label={`Tags: ${filters.tag.value.map(tagId => {
                                const tag = tags.find(t => t.id === tagId);
                                return tag ? tag.label : `ID ${tagId}`;
                            }).join(', ')}`}
                            onDelete={canRemove('tag') ? () => onRemoveFilter('tag') : undefined}
                            variant="filled"
                            size="small"
                            sx={{ 
                                backgroundColor: '#FFD700',
                                color: '#333333',
                                '& .MuiChip-label': { 
                                    fontWeight: 'normal',
                                    color: '#333333'
                                },
                                '& .MuiChip-icon': {
                                    color: '#333333'
                                },
                                '& .MuiChip-deleteIcon': {
                                    color: '#333333',
                                    '&:hover': {
                                        color: '#000000'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#FFC107'
                                }
                            }}
                        />
                    </Tooltip>
                )}
                {filters.sentiment && (
                    <Tooltip title="Sentiment filter applied by clicking on chart">
                        <Chip
                            icon={<FilterAlt />}
                            label={`Sentiment: ${sentiments[filters.sentiment] || filters.sentiment}`}
                            onDelete={canRemove('sentiment') ? () => onRemoveFilter('sentiment') : undefined}
                            variant="filled"
                            size="small"
                            sx={{ 
                                backgroundColor: '#FFD700',
                                color: '#333333',
                                '& .MuiChip-label': { 
                                    fontWeight: 'normal',
                                    color: '#333333'
                                },
                                '& .MuiChip-icon': {
                                    color: '#333333'
                                },
                                '& .MuiChip-deleteIcon': {
                                    color: '#333333',
                                    '&:hover': {
                                        color: '#000000'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#FFC107'
                                }
                            }}
                        />
                    </Tooltip>
                )}
                {filters.language && (
                    <Tooltip title="Language filter applied by clicking on chart">
                        <Chip
                            icon={<FilterAlt />}
                            label={`Language: ${languages[filters.language] || filters.language}`}
                            onDelete={canRemove('language') ? () => onRemoveFilter('language') : undefined}
                            variant="filled"
                            size="small"
                            sx={{ 
                                backgroundColor: '#FFD700',
                                color: '#333333',
                                '& .MuiChip-label': { 
                                    fontWeight: 'normal',
                                    color: '#333333'
                                },
                                '& .MuiChip-icon': {
                                    color: '#333333'
                                },
                                '& .MuiChip-deleteIcon': {
                                    color: '#333333',
                                    '&:hover': {
                                        color: '#000000'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#FFC107'
                                }
                            }}
                        />
                    </Tooltip>
                )}
                {filters.date && filters.date.start && filters.date.end && (
                    <Tooltip title="Date range applied by clicking on chart">
                        <Chip
                            icon={<FilterAlt />}
                            label={`Date: ${formatDateRange(filters.date)}`}
                            onDelete={canRemove('date') ? () => onRemoveFilter('date') : undefined}
                            variant="filled"
                            size="small"
                            sx={{ 
                                backgroundColor: '#FFD700',
                                color: '#333333',
                                '& .MuiChip-label': { 
                                    fontWeight: 'normal',
                                    color: '#333333'
                                },
                                '& .MuiChip-icon': {
                                    color: '#333333'
                                },
                                '& .MuiChip-deleteIcon': {
                                    color: '#333333',
                                    '&:hover': {
                                        color: '#000000'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: '#FFC107'
                                }
                            }}
                        />
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
}

export default ChartInteractionIndicator;